import { useState, useMemo, useCallback, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Search, Loader2, Package } from "lucide-react";
import { toast } from "sonner";

import {
  useCollectionProducts,
  useStorefrontProducts,
  useAssignProducts,
  useRemoveProducts,
  type CollectionProduct,
} from "./hooks";
import { useAuthStore } from "@/lib/store/authStore";

interface CollectionProductsTabProps {
  tenantKey: string | null;
  collectionName: string;
}

export function CollectionProductsTab({
  tenantKey,
  collectionName,
}: CollectionProductsTabProps) {
  // activeStorefrontKey prepared for future per-store collection filtering
  void useAuthStore((s) => s.activeStorefrontKey);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedCodes, setSelectedCodes] = useState<Set<string>>(new Set());
  const [initialCodes, setInitialCodes] = useState<Set<string>>(new Set());
  const [hasInitialized, setHasInitialized] = useState(false);

  // Debounce search input (300ms)
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Fetch currently assigned products
  const {
    data: assignedProducts,
    isLoading: assignedLoading,
  } = useCollectionProducts(tenantKey, collectionName);

  // Fetch all storefront-enabled products (with server-side search)
  const {
    data: allProducts,
    isLoading: productsLoading,
  } = useStorefrontProducts(debouncedSearch, !!tenantKey);

  // Mutations
  const assignMutation = useAssignProducts(tenantKey);
  const removeMutation = useRemoveProducts(tenantKey);
  const isSaving = assignMutation.isPending || removeMutation.isPending;

  // Initialize selected state from assigned products
  useEffect(() => {
    if (assignedProducts && !hasInitialized) {
      const codes = new Set(assignedProducts.map((p) => p.item_code));
      setSelectedCodes(codes);
      setInitialCodes(codes);
      setHasInitialized(true);
    }
  }, [assignedProducts, hasInitialized]);

  // Reset initialization when collection changes
  useEffect(() => {
    setHasInitialized(false);
  }, [collectionName]);

  // Build the display list: assigned products at top, then unassigned
  const displayList = useMemo(() => {
    if (!allProducts) return [];

    const assignedSet = new Set(
      (assignedProducts ?? []).map((p) => p.item_code)
    );

    // Build a lookup map for assigned products (for disabled badge)
    const assignedMap = new Map<string, CollectionProduct>();
    (assignedProducts ?? []).forEach((p) => {
      assignedMap.set(p.item_code, p);
    });

    // Map all products to a unified shape
    const items: DisplayProduct[] = allProducts.map((item) => {
      const assigned = assignedSet.has(item.name);
      const assignedData = assignedMap.get(item.name);
      return {
        item_code: item.name,
        item_name: item.item_name,
        image: item.image,
        item_group: item.item_group,
        disabled: item.disabled === 1,
        storefront_enabled: item.storefront_enabled === 1,
        is_assigned: assigned,
        // A product is "inactive" if disabled or not storefront_enabled
        is_inactive:
          item.disabled === 1 ||
          item.storefront_enabled !== 1 ||
          (assignedData ? assignedData.disabled : false),
      };
    });

    // Sort: assigned first, then alphabetically by name
    items.sort((a, b) => {
      if (a.is_assigned !== b.is_assigned) return a.is_assigned ? -1 : 1;
      return a.item_name.localeCompare(b.item_name);
    });

    return items;
  }, [allProducts, assignedProducts]);

  const handleToggle = useCallback((itemCode: string) => {
    setSelectedCodes((prev) => {
      const next = new Set(prev);
      if (next.has(itemCode)) {
        next.delete(itemCode);
      } else {
        next.add(itemCode);
      }
      return next;
    });
  }, []);

  // Compute diff
  const { toAssign, toRemove } = useMemo(() => {
    const toAssign: string[] = [];
    const toRemove: string[] = [];

    // New selections: in selectedCodes but not in initialCodes
    selectedCodes.forEach((code) => {
      if (!initialCodes.has(code)) toAssign.push(code);
    });

    // Removals: in initialCodes but not in selectedCodes
    initialCodes.forEach((code) => {
      if (!selectedCodes.has(code)) toRemove.push(code);
    });

    return { toAssign, toRemove };
  }, [selectedCodes, initialCodes]);

  const hasChanges = toAssign.length > 0 || toRemove.length > 0;

  const handleSave = useCallback(async () => {
    try {
      const promises: Promise<unknown>[] = [];

      if (toAssign.length > 0) {
        promises.push(
          assignMutation.mutateAsync({
            collection_name: collectionName,
            item_codes: toAssign,
          })
        );
      }

      if (toRemove.length > 0) {
        promises.push(
          removeMutation.mutateAsync({
            collection_name: collectionName,
            item_codes: toRemove,
          })
        );
      }

      await Promise.all(promises);

      // Update initial codes to reflect saved state
      setInitialCodes(new Set(selectedCodes));

      const parts: string[] = [];
      if (toAssign.length > 0) parts.push(`${toAssign.length} added`);
      if (toRemove.length > 0) parts.push(`${toRemove.length} removed`);
      toast.success(`Products updated: ${parts.join(", ")}.`);
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Failed to update products.";
      toast.error(message);
    }
  }, [
    toAssign,
    toRemove,
    collectionName,
    assignMutation,
    removeMutation,
    selectedCodes,
  ]);

  const isLoading = assignedLoading || (productsLoading && !allProducts);

  return (
    <div className="flex flex-col h-full">
      {/* Search */}
      <div className="px-6 py-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search products by name or code..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-10 rounded text-sm"
          />
        </div>
      </div>

      {/* Product list */}
      <div className="flex-1 overflow-y-auto px-6">
        {isLoading && (
          <div className="space-y-2">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex items-center gap-3 py-2">
                <Skeleton className="h-4 w-4 rounded-[2px]" />
                <Skeleton className="h-8 w-8 rounded-xs" />
                <div className="flex-1 space-y-1">
                  <Skeleton className="h-3.5 w-32" />
                  <Skeleton className="h-3 w-24" />
                </div>
              </div>
            ))}
          </div>
        )}

        {!isLoading && displayList.length === 0 && (
          <div className="flex flex-col items-center gap-3 py-12 text-center">
            <Package className="h-8 w-8 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">
              {search
                ? "No products match your search."
                : "No storefront-enabled products found."}
            </p>
          </div>
        )}

        {!isLoading && displayList.length > 0 && (
          <div className="space-y-0.5">
            {displayList.map((item) => (
              <ProductRow
                key={item.item_code}
                item={item}
                checked={selectedCodes.has(item.item_code)}
                onToggle={handleToggle}
              />
            ))}
          </div>
        )}
      </div>

      {/* Sticky footer */}
      <div className="border-t px-6 py-3 flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {selectedCodes.size} product{selectedCodes.size !== 1 ? "s" : ""}{" "}
          selected
          {hasChanges && (
            <span className="text-foreground font-medium ml-1">
              ({toAssign.length > 0 && `+${toAssign.length}`}
              {toAssign.length > 0 && toRemove.length > 0 && ", "}
              {toRemove.length > 0 && `-${toRemove.length}`})
            </span>
          )}
        </p>
        <Button
          size="sm"
          onClick={handleSave}
          disabled={!hasChanges || isSaving}
          className="rounded"
        >
          {isSaving && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
          Save
        </Button>
      </div>
    </div>
  );
}

// ── Types ───────────────────────────────────────────────────

interface DisplayProduct {
  item_code: string;
  item_name: string;
  image: string | null;
  item_group: string | null;
  disabled: boolean;
  storefront_enabled: boolean;
  is_assigned: boolean;
  is_inactive: boolean;
}

// ── Product Row ─────────────────────────────────────────────

interface ProductRowProps {
  item: DisplayProduct;
  checked: boolean;
  onToggle: (itemCode: string) => void;
}

function ProductRow({ item, checked, onToggle }: ProductRowProps) {
  return (
    <label
      className={`flex items-center gap-3 rounded px-2 py-2 cursor-pointer transition-colors ${
        checked
          ? "bg-teal-50/60 dark:bg-teal-900/10"
          : "hover:bg-muted/30"
      } ${item.is_inactive ? "opacity-50" : ""}`}
    >
      <Checkbox
        checked={checked}
        onCheckedChange={() => onToggle(item.item_code)}
        aria-label={`${item.item_name}, ${item.item_code}${item.item_group ? `, ${item.item_group}` : ""}`}
      />

      {/* Thumbnail */}
      <div className="h-8 w-8 flex-shrink-0 rounded-xs bg-muted/40 overflow-hidden">
        {item.image ? (
          <img
            src={item.image}
            alt={item.item_name}
            className="h-full w-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="h-full w-full flex items-center justify-center">
            <Package className="h-3.5 w-3.5 text-muted-foreground/40" />
          </div>
        )}
      </div>

      {/* Info */}
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium truncate">{item.item_name}</p>
        <p className="text-xs text-muted-foreground font-mono truncate">
          {item.item_code}
        </p>
      </div>

      {/* Badges */}
      <div className="flex items-center gap-1.5 flex-shrink-0">
        {item.item_group && (
          <Badge
            variant="secondary"
            className="rounded-full text-[10px] font-normal max-w-[100px] truncate"
          >
            {item.item_group}
          </Badge>
        )}
        {item.disabled && (
          <Badge className="rounded-full text-[10px] bg-orange-100 text-orange-700 border-0">
            Disabled
          </Badge>
        )}
      </div>
    </label>
  );
}
